/**
 * Blue Room Studio — Three.js viewer for Shopify OS 2.0
 *
 * Include this asset as a module, for example:
 *   <script type="module" src="{{ 'br-three-engine.js' | asset_url }}"></script>
 *
 * Then initialise it from a theme section or another module:
 *   const studio = new BRStudio('#BRStudio', { autoStart: true });
 *
 * The target element needs a visible height (via its CSS or an inline style).
 */

import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

const DEFAULT_MODEL_URL =
  'https://cdn.shopify.com/3d/models/2022cc6dc90d95c2/Couch_Wide_by_Danni_Bittman_-_7Q_Ab2HLll1.glb';

/**
 * A self-contained product viewer.  One instance owns one canvas and can be
 * safely destroyed when Shopify's section editor unloads a section.
 */
class BRStudio {
  /**
   * @param {string|HTMLElement} container - Element, or selector for it.
   * @param {object} options - Viewer configurati
   */
  constructor(container, options = {}) {
    this.options = {
      modelUrl: DEFAULT_MODEL_URL,
      background: 0xf4f2ee,
      autoRotate: false,
      autoRotateSpeed: 1.2,
      cameraFov: 38,
      cameraNear: 0.1,
      cameraFar: 100,
      shadows: true,
      showFloor: true,
      showCube: false,
      autoStart: true,
      onLoad: null,
      onProgress: null,
      onError: null,
      ...options,
    };

  this.container =
  typeof container === "string"
    ? document.querySelector(container)
    : container;

    if (!(this.container instanceof HTMLElement)) {
      throw new Error('BRStudio: a valid container element is required.');
    }

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.clock = new THREE.Clock();
    /*
==========================================
SCENE ASSETS
==========================================
*/

this.assets = new Map();
    this.floor = null;
    this.animationFrame = null;
    this.isRunning = false;
    this.resizeObserver = null;
    this.onResize = this.onResize.bind(this);

    this.init();
  }

  /** Build the viewer and begin loading the configured GLB model. */
  init() {
    this.createScene();
    this.createCamera();
    this.createRenderer();
    this.createControls();
    this.createLights();

    if (this.options.showFloor) this.createFloor();
    if (this.options.showCube) this.createCube();

    this.observeResize();
    this.loadModel(this.options.modelUrl);

    if (this.options.autoStart) this.animate();
  }

  /** Create the scene with a soft, studio-style background. */
createScene() {

    this.scene = new THREE.Scene();

    this.scene.background = new THREE.Color(this.options.background);

    // ==========================
    // DEBUG
    // ==========================

    const axes = new THREE.AxesHelper(5);

    this.scene.add(axes);

    const grid = new THREE.GridHelper(20, 20);

    this.scene.add(grid);

}

  /** Create a perspective camera sized for a furniture product viewer. */
  createCamera() {
    const { width, height } = this.getSize();
    this.camera = new THREE.PerspectiveCamera(
      this.options.cameraFov,
      width / height,
      this.options.cameraNear,
      this.options.cameraFar,
    );
    this.camera.position.set(3.8, 2.4, 4.8);
  }

  /** Create and attach the WebGL canvas. */
  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.shadowMap.enabled = this.options.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.domElement.className = 'br-studio__canvas';
    this.renderer.domElement.setAttribute('aria-label', 'Vista 3D del producto');
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.container.appendChild(this.renderer.domElement);
    this.onResize();
  }

  /** Enable touch, mouse, and keyboard-friendly orbiting around the product. */
  createControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.enablePan = false;
    this.controls.minDistance = 1.5;
    this.controls.maxDistance = 12;
    this.controls.maxPolarAngle = Math.PI * 0.48;
    this.controls.autoRotate = this.options.autoRotate;
    this.controls.autoRotateSpeed = this.options.autoRotateSpeed;
    this.controls.target.set(0, 0.8, 0);
    this.controls.update();
  }

  /** Add balanced key, fill, rim, and ambient studio lighting. */
  createLights() {
    const hemisphere = new THREE.HemisphereLight(0xffffff, 0x7b7068, 2.2);
    this.scene.add(hemisphere);

    const key = new THREE.DirectionalLight(0xfff5e8, 3.5);
    key.position.set(4, 6, 4);
    key.castShadow = this.options.shadows;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 0.1;
    key.shadow.camera.far = 20;
    key.shadow.camera.left = -6;
    key.shadow.camera.right = 6;
    key.shadow.camera.top = 6;
    key.shadow.camera.bottom = -6;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0xdbe8ff, 1.5);
    fill.position.set(-5, 3, 2);
    this.scene.add(fill);

    const rim = new THREE.DirectionalLight(0xffffff, 2);
    rim.position.set(-2, 4, -5);
    this.scene.add(rim);
  }

  /** Add a matte floor that receives the product's soft shadow. */
  createFloor() {
    const geometry = new THREE.PlaneGeometry(30, 30);
    const material = new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.18 });
    this.floor = new THREE.Mesh(geometry, material);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.receiveShadow = this.options.shadows;
    this.floor.position.y = 0;
    this.scene.add(this.floor);
  }

  /**
   * Add a small reference cube. It is off by default and useful only while
   * positioning a new model in development.
   */
  createCube() {
    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(0.25, 0.25, 0.25),
      new THREE.MeshStandardMaterial({ color: 0x276ef1 }),
    );
    cube.position.set(0, 0.125, 0);
    cube.castShadow = true;
    cube.name = 'BRStudioReferenceCube';
    this.scene.add(cube);
    return cube;
  }
/**
 * Load a GLB file and return its scene.
 */
loadGLB(url){

    return new Promise((resolve,reject)=>{

        const loader = new GLTFLoader();

        loader.load(

            url,

            (gltf)=>{

                resolve(gltf);

            },

            (event)=>{

                const progress = event.total
                    ? event.loaded / event.total
                    : 0;

                this.options.onProgress?.(
                    progress,
                    event,
                    this
                );

            },

            (error)=>{

                console.error(
                    "BRStudio: unable to load the 3D model.",
                    error
                );

                this.options.onError?.(
                    error,
                    this
                );

                reject(error);

            }

        );

    });

}
/**
 * Replace an asset in the scene.
 *
 * Example:
 * await replaceAsset("sofa", url);
 */
async replaceAsset(type, url) {

    const previousAsset = this.assets.get(type);

    if (previousAsset) {

        this.disposeObject(previousAsset);

    }

    const gltf = await this.loadGLB(url);

    const asset = gltf.scene;

    asset.traverse((child) => {

        if (!child.isMesh) return;

        child.castShadow = this.options.shadows;

        child.receiveShadow = this.options.shadows;

        const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];

        materials
            .filter(Boolean)
            .forEach((material) => {

                if (material.map) {

                    material.map.colorSpace =
                        THREE.SRGBColorSpace;

                }

                material.needsUpdate = true;

            });

    });

    this.scene.add(asset);

    this.assets.set(type, asset);

    if (type === "sofa") {

        this.centerModel(asset);

    }

    this.options.onLoad?.(gltf, this);

    return asset;

}
/**
 * Add an asset without replacing existing ones.
 */
async addAsset(type, url) {

    if (this.assets.has(type)) {

        console.warn(type + " already exists.");

        return this.assets.get(type);

    }

    const gltf = await this.loadGLB(url);

    const asset = gltf.scene;

    // ====================================
    // DEBUG
    // ====================================

const box = new THREE.Box3().setFromObject(asset);

const size = box.getSize(new THREE.Vector3());

const largestDimension = Math.max(
    size.x,
    size.y,
    size.z
);

// Tamaño objetivo según el tipo de objeto
const targetSizes = {
    sofa: 3.0,
    table: 1.8,
    chair: 1.0,
    lamp: 0.6,
    rug: 2.5
};

const targetSize = targetSizes[type] ?? 2.0;

// Escala automática
const scale = targetSize / largestDimension;

asset.scale.setScalar(scale);

// Recalcular el Box después de escalar
const scaledBox = new THREE.Box3().setFromObject(asset);

const center = scaledBox.getCenter(new THREE.Vector3());

asset.position.sub(center);

// Posición de prueba
asset.position.x += 2.5;

// Piso
asset.position.y -= scaledBox.min.y;

    // ====================================

    asset.traverse((child) => {

        if (!child.isMesh) return;

        child.castShadow = this.options.shadows;

        child.receiveShadow = this.options.shadows;

        const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];

        materials.forEach((material) => {

            if (!material) return;

            if (material.map) {

                material.map.colorSpace = THREE.SRGBColorSpace;

            }

            material.needsUpdate = true;

        });

    });

    this.scene.add(asset);

    this.assets.set(type, asset);

    return asset;

}
/**
 * Get an asset from the scene.
 */
getAsset(type){

    return this.assets.get(type);

}
/**
 * Remove an asset.
 */
removeAsset(type){

    const asset = this.assets.get(type);

    if(!asset) return;

    this.disposeObject(asset);

    this.assets.delete(type);

}
/**
 * Check if an asset exists.
 */
hasAsset(type){

    return this.assets.has(type);

}
/**
 * Backwards compatibility.
 * Internally the Scene Engine now uses replaceAsset().
 */
async loadModel(url) {

    return await this.replaceAsset(
        "sofa",
        url
    );

}
/** Centre the current model on the floor and adjust the camera to frame it. */
centerModel(model) {

    if (!model) return;

    const box = new THREE.Box3().setFromObject(model);

    if (box.isEmpty()) return;

    const center = box.getCenter(new THREE.Vector3());

    const size = box.getSize(new THREE.Vector3());

    // Offset X/Z to the origin and place the lowest point exactly on the floor.

    model.position.x -= center.x;

    model.position.z -= center.z;

    model.position.y -= box.min.y;

    const largestDimension = Math.max(size.x, size.y, size.z);

    const halfFov = THREE.MathUtils.degToRad(this.camera.fov * 0.5);

    const distance = (largestDimension * 0.6) / Math.tan(halfFov);

    this.camera.position.set(
        distance * 1.15,
        distance * 0.7,
        distance * 1.35
    );

    this.camera.near = Math.max(largestDimension / 100, 0.01);

    this.camera.far = Math.max(largestDimension * 20, 100);

    this.camera.updateProjectionMatrix();

    this.controls.target.set(
        0,
        size.y * 0.42,
        0
    );

    this.controls.minDistance = Math.max(
        largestDimension * 0.45,
        0.7
    );

    this.controls.maxDistance = Math.max(
        largestDimension * 4,
        8
    );

    this.controls.update();

}

  /** Start the render loop. Calling it more than once is harmless. */
  animate() {
    if (this.isRunning) return;
    this.isRunning = true;

    const render = () => {
      if (!this.isRunning) return;
      this.animationFrame = window.requestAnimationFrame(render);
      this.clock.getDelta();
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };

    render();
  }

  /** Stop the render loop without removing the canvas. */
  stop() {
    this.isRunning = false;
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }

  /** Update canvas and camera proportions after the section changes size. */
  onResize() {
    if (!this.renderer || !this.camera) return;
    const { width, height } = this.getSize();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  /** Observe Shopify editor changes as well as normal browser resizes. */
  observeResize() {
    window.addEventListener('resize', this.onResize);
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.container);
    }
  }

  /** Return safe non-zero dimensions even before CSS has finished loading. */
  getSize() {
    return {
      width: Math.max(this.container.clientWidth, 1),
      height: Math.max(this.container.clientHeight, 1),
    };
  }

  /** Dispose GPU resources and remove all listeners/canvas nodes. */
  destroy() {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
    this.controls?.dispose();
    this.assets.forEach((asset) => {

    this.disposeObject(asset);

});

this.assets.clear();

    this.floor?.geometry.dispose();
    this.floor?.material.dispose();
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
  }

  /** Dispose geometries, materials, and textures owned by an Object3D tree. */
  disposeObject(object) {
    object.traverse((child) => {
      if (!child.isMesh) return;
      child.geometry?.dispose();
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.filter(Boolean).forEach((material) => {
        Object.values(material).forEach((value) => {
          if (value?.isTexture) value.dispose();
        });
        material.dispose();
      });
    });
    object.removeFromParent();
  }
}

// Make the class available to regular Shopify section scripts as well as ESM imports.
window.BRStudio = BRStudio;

export { BRStudio, DEFAULT_MODEL_URL };
export default BRStudio;
let studio = null;

document.addEventListener("DOMContentLoaded", () => {

    const container = document.querySelector("#br-three-canvas");

    if (!container) return;

    studio = new BRStudio(container);

    const demoButton = document.getElementById("br-add-demo");

    if (demoButton) {

        demoButton.addEventListener("click", async () => {

            await studio.addAsset(

                "table",

                "https://cdn.shopify.com/3d/models/e7fbd9482505e1a8/Table_by_CMHT_Oculus_-_asZ_nKHursY.glb"

            );

        });

    }

});